import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DgbSymbol } from '@/components/brand-logo';
import {
  creep,
  displayedProgress,
  easeOut,
  floorOf,
  markStartFinished,
  SETTLE_MS,
  setDisplayedProgress,
  STEP_VALUES,
  type StartStep,
} from './start-progress';

/** Each step's text, in the order of `STEP_VALUES`. */
const STEP_TEXTS = ['common:start.session', 'common:start.access', 'common:start.space'] as const;

/** The fade once the bar is full, in ms (the `.start-screen` transition in `globals.css`). */
const FADE_MS = 220;

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;
}

type StartScreenProps = {
  /** The step running now: 0 restores the session, 1 checks access, 2 prepares the space. */
  readonly step: StartStep;
  /** The step is done: the bar eases to its value, and at 100% the screen fades. */
  readonly done?: boolean;
  /** Called once the screen has faded, after `done` on the last step. */
  readonly onFinished?: () => void;
};

/**
 * The start screen (#430, decision 112): the mark, «DGB Console», the running step and a bar that
 * tells the truth. Each gate that waits on the network at start-up draws it for its own step; the
 * bar's value lives in `start-progress.ts`, so it carries on from one gate to the next.
 *
 * On mount the bar first eases to the floor of its step (the previous step is done, or this gate
 * would not be mounted), then creeps under the step's value until `done`. The bar is written
 * straight to the DOM on each frame: a React render per frame would be work for nothing.
 */
export function StartScreen({ step, done = false, onFinished }: StartScreenProps) {
  const { t } = useTranslation(['common']);
  const rootRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLSpanElement>(null);
  const [leaving, setLeaving] = useState(false);
  const onFinishedRef = useRef(onFinished);
  onFinishedRef.current = onFinished;

  useEffect(() => {
    const reduced = prefersReducedMotion();
    const target = done ? STEP_VALUES[step] : floorOf(step);
    let frameId = 0;
    let fadeId: ReturnType<typeof setTimeout> | undefined;

    const paint = (value: number) => {
      setDisplayedProgress(value);
      const shown = displayedProgress();
      if (fillRef.current) {
        fillRef.current.style.width = `${shown.toFixed(2)}%`;
      }
      rootRef.current?.setAttribute('aria-valuenow', String(Math.round(shown)));
    };

    const finish = () => {
      if (step !== STEP_VALUES.length - 1) {
        return;
      }
      markStartFinished();
      setLeaving(true);
      fadeId = setTimeout(() => onFinishedRef.current?.(), reduced ? 0 : FADE_MS);
    };

    if (reduced) {
      paint(target);
      if (done) {
        finish();
      }
      return () => clearTimeout(fadeId);
    }

    const from = displayedProgress();
    let settleStart: number | null = from < target ? performance.now() : null;
    let last = performance.now();

    if (settleStart === null && done) {
      finish();
      return () => clearTimeout(fadeId);
    }

    const frame = (now: number) => {
      const dt = now - last;
      last = now;
      if (settleStart !== null) {
        const k = (now - settleStart) / SETTLE_MS;
        paint(from + (target - from) * easeOut(k));
        if (k >= 1) {
          settleStart = null;
          if (done) {
            finish();
            return;
          }
        }
      } else {
        paint(creep(displayedProgress(), step, dt));
      }
      frameId = requestAnimationFrame(frame);
    };
    frameId = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(fadeId);
    };
  }, [step, done]);

  const initial = displayedProgress();

  return (
    <div
      ref={rootRef}
      className="start-screen"
      data-leaving={leaving ? '' : undefined}
      role="progressbar"
      aria-label={t('common:start.label')}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(initial)}
    >
      <DgbSymbol className="start-screen-mark" alt="" />
      <p className="start-screen-name">DGB Console</p>
      <p className="start-screen-step">{t(STEP_TEXTS[step])}</p>
      <div className="start-screen-bar">
        <span ref={fillRef} className="start-screen-fill" style={{ width: `${initial}%` }} />
      </div>
    </div>
  );
}
